// Webhook types and interfaces
export interface Webhook {
  id: string;
  url: string;
  events: string[];
  secret?: string;
  createdAt: Date;
  lastTriggered?: Date;
  active: boolean;
}

export interface WebhookPayload {
  event: string;
  timestamp: string;
  data: any;
}

// Local storage key for webhooks
const WEBHOOKS_KEY = 'installtrack_webhooks';

// Get all registered webhooks
export const getWebhooks = (): Webhook[] => {
  try {
    const webhooksJson = localStorage.getItem(WEBHOOKS_KEY);
    if (!webhooksJson) return [];
    
    return JSON.parse(webhooksJson, (key, value) => {
      if (key === 'createdAt' || key === 'lastTriggered') {
        return value ? new Date(value) : undefined;
      }
      return value;
    });
  } catch (error) {
    console.error('Error getting webhooks:', error);
    return [];
  }
};

// Save webhooks to local storage
const saveWebhooks = (webhooks: Webhook[]): void => {
  try {
    localStorage.setItem(WEBHOOKS_KEY, JSON.stringify(webhooks));
  } catch (error) {
    console.error('Error saving webhooks:', error);
  }
};

// Register a new webhook
export const registerWebhook = (
  url: string,
  events: string[],
  secret?: string
): Webhook => {
  if (!url) {
    throw new Error('URL is required');
  }
  
  if (!events || events.length === 0) {
    throw new Error('At least one event is required');
  }
  
  // Validate URL format
  try {
    new URL(url);
  } catch (error) {
    throw new Error('Invalid URL format');
  }
  
  const webhooks = getWebhooks();
  
  // Check if webhook with this URL already exists
  const existingWebhook = webhooks.find(webhook => webhook.url === url);
  if (existingWebhook) {
    throw new Error('Webhook with this URL already exists');
  }
  
  const newWebhook: Webhook = {
    id: `wh_${Math.random().toString(36).substring(2, 15)}`,
    url,
    events,
    secret,
    createdAt: new Date(),
    active: true
  };
  
  webhooks.push(newWebhook);
  saveWebhooks(webhooks);
  
  return newWebhook;
};

// Update an existing webhook
export const updateWebhook = (
  id: string,
  updates: Partial<Omit<Webhook, 'id' | 'createdAt'>>
): Webhook => {
  const webhooks = getWebhooks();
  const webhookIndex = webhooks.findIndex(webhook => webhook.id === id);
  
  if (webhookIndex === -1) {
    throw new Error('Webhook not found');
  }
  
  // If URL is being updated, validate it
  if (updates.url) {
    try {
      new URL(updates.url);
    } catch (error) {
      throw new Error('Invalid URL format');
    }
    
    // Check if another webhook already uses this URL
    const duplicateUrl = webhooks.some(
      (webhook, index) => index !== webhookIndex && webhook.url === updates.url
    );
    
    if (duplicateUrl) {
      throw new Error('Another webhook with this URL already exists');
    }
  }
  
  // Update the webhook
  webhooks[webhookIndex] = {
    ...webhooks[webhookIndex],
    ...updates
  };
  
  saveWebhooks(webhooks);
  
  return webhooks[webhookIndex];
};

// Delete a webhook
export const deleteWebhook = (id: string): boolean => {
  const webhooks = getWebhooks();
  const initialLength = webhooks.length;
  
  const filteredWebhooks = webhooks.filter(webhook => webhook.id !== id);
  
  if (filteredWebhooks.length === initialLength) {
    return false; // No webhook was deleted
  }
  
  saveWebhooks(filteredWebhooks);
  return true;
};

// Trigger webhooks for an event
export const triggerWebhook = async (
  event: string,
  data: any
): Promise<void> => {
  const webhooks = getWebhooks();
  const relevantWebhooks = webhooks.filter(
    webhook => webhook.active && (
      webhook.events.includes(event) || 
      webhook.events.includes('*')
    )
  );
  
  if (relevantWebhooks.length === 0) {
    return; // No webhooks to trigger
  }
  
  const timestamp = new Date().toISOString();
  
  // Prepare the payload
  const payload: WebhookPayload = {
    event,
    timestamp,
    data
  };
  
  // Trigger each webhook
  for (const webhook of relevantWebhooks) {
    try {
      // In a real implementation, we would use fetch to send the payload
      // For this demo, we'll just log it
      console.log(`Triggering webhook ${webhook.id} for event ${event} to ${webhook.url}`);
      console.log('Payload:', payload);
      
      // If we were actually sending the request:
      /*
      const response = await fetch(webhook.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(webhook.secret ? { 'X-Webhook-Signature': generateSignature(payload, webhook.secret) } : {})
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      */
      
      // Update last triggered timestamp
      updateWebhook(webhook.id, {
        lastTriggered: new Date()
      });
    } catch (error) {
      console.error(`Error triggering webhook ${webhook.id}:`, error);
      // In a real implementation, we might want to track failed webhooks
      // and retry them later or disable them after too many failures
    }
  }
};

// Generate a signature for webhook payloads
const generateSignature = (payload: any, secret: string): string => {
  // In a real implementation, we would use a crypto library to generate an HMAC
  // For this demo, we'll just return a placeholder
  return `sha256=${secret}_${Date.now()}`;
};

// List of available webhook events
export const availableWebhookEvents = [
  'building.created',
  'building.updated',
  'building.deleted',
  'apartment.updated',
  'photo.uploaded',
  'photo.deleted',
  '*' // Wildcard for all events
];